// Drive-synced files for one project, rendered as a table mirroring
// the regular files list. Folders are rows that span all columns and
// collapse / expand on click; files render with the filename as the
// clickable Drive link (no separate Actions column - keeps the
// layout from getting squeezed by a long ``Open in Drive`` button).
//
// Top-level groups (one per synced root folder, plus Tracked files) start
// expanded; deeper folders stay collapsed until clicked. Folder rows show
// ``Name (N)`` where N is the recursive file count under that folder
// so the operator can scan structure without expanding everything.
import { RegisterBadge } from "./RegisterBadge";
import { useMemo, useState } from "react";

import {
  kindLabel,
  UploadsExtractedProvider,
  useSetUploadFreshness,
  useUploadExtracted,
} from "../api";
import {
  DriveSyncState,
  TRACKED_PARENT,
  UploadsTreeEntry,
  useDriveSyncOperation,
  useDismissUpload,
  useExcludeDriveFile,
  useReinstateDriveFile,
  useTriggerDriveSync,
  useUploadsTree,
} from "../api/hooks/useDriveSync";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useProject } from "../api/hooks/useProject";
import { ExtractedContentPanel } from "./ExtractedContentPanel";
import { ExtractionStatusButton } from "./ExtractionStatusButton";
import { OutOfDateIcon } from "./OutOfDateIcon";
import { ReextractLink } from "./ReextractLink";
import { removalReasonLabel } from "./removalReason";
import { dedupeRenderedSightings, SightingList } from "./SightingList";
import { SyncStatusLine } from "./SyncStatusLine";
import styles from "../routes/ProjectFilesRoute.module.css";
import {
  buildDriveFilesTree,
  countTreeFiles,
  sortedChildren,
  sortedFiles,
  useDriveTreeOpenState,
} from "../utils/driveFilesTree";
import type { DriveFilesNode } from "../utils/driveFilesTree";
import { formatBytes } from "../utils/formatBytes";
import { Chevron } from "./Chevron";

interface Props {
  projectId: string;
}

type FolderNode = DriveFilesNode<UploadsTreeEntry>;

// True when a file is out of date - mirrors the FileRow definition so the
// folder roll-up and the per-row clock icon agree (file-level marked stale,
// or stale on at least one field it contributes).
function isOutOfDate(file: UploadsTreeEntry): boolean {
  return file.marked_out_of_date || (file.out_of_date_fields?.length ?? 0) > 0;
}

// Recursively counts the descendant files that failed extraction and the ones
// that are out of date, so each folder row can show how many of each sit
// somewhere under it without the operator having to expand the tree.
// Non-active rows (``removed`` tombstones and wrong-site ``quarantined`` rows)
// are excluded from both - they're already out of the canonical pipeline, a
// "failed"/"stale" status on one isn't actionable, and counting them would put
// this chip at odds with the Files tab's summary bar, which skips both.
interface FolderHealthCounts {
  failed: number;
  outOfDate: number;
}

function countFolderHealth(node: FolderNode): FolderHealthCounts {
  const counts: FolderHealthCounts = { failed: 0, outOfDate: 0 };
  for (const file of node.files) {
    if (file.status === "removed" || file.status === "quarantined") continue;
    if (file.extraction_status === "failed") counts.failed += 1;
    if (isOutOfDate(file)) counts.outOfDate += 1;
  }
  for (const child of node.children.values()) {
    const childCounts = countFolderHealth(child);
    counts.failed += childCounts.failed;
    counts.outOfDate += childCounts.outOfDate;
  }
  return counts;
}

// True when this folder's entire subtree is files removed SOLELY because the
// folder itself is excluded in the project's Drive-sync config. Such a folder
// is pure noise - every row is a "Folder is excluded in this project's config"
// tombstone - so the tree hides it rather than showing a struck-through wall of
// rows the operator can't act on. A folder with any non-excluded file (or a
// file removed for any other reason - operator-excluded, deleted-in-Drive)
// still renders. Requires >= 1 file in the subtree so a genuinely empty folder
// isn't swept up.
function isEntirelyFolderExcluded(node: FolderNode): boolean {
  let sawFile = false;
  for (const file of node.files) {
    sawFile = true;
    if (file.status !== "removed" || file.removal_reason !== "folder_excluded_in_config") {
      return false;
    }
  }
  for (const child of node.children.values()) {
    if (countTreeFiles(child) === 0) continue;
    sawFile = true;
    if (!isEntirelyFolderExcluded(child)) return false;
  }
  return sawFile;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  } catch {
    return iso;
  }
}

function driveUrl(fileId: string | null): string | null {
  if (!fileId) return null;
  return `https://drive.google.com/file/d/${encodeURIComponent(fileId)}/view`;
}

interface FileRowProps {
  entry: UploadsTreeEntry;
  projectId: string;
  depth: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onDismiss: (uploadId: string) => void;
  dismissBusy: boolean;
  onExclude: (uploadId: string) => void;
  excludeBusy: boolean;
  onReinstate: (uploadId: string) => void;
  reinstateBusy: boolean;
}

const FileRow = ({
  entry,
  projectId,
  depth,
  expanded,
  onToggleExpand,
  onDismiss,
  dismissBusy,
  onExclude,
  excludeBusy,
  onReinstate,
  reinstateBusy,
}: FileRowProps) => {
  const isTombstone = entry.status === "removed";
  const baseReasonText = removalReasonLabel(entry.removal_reason);
  // For operator-excluded rows surface the who/when audit info so the
  // tombstone carries its own provenance ("Excluded by Noaa Cohn on
  // 2026-06-09"). Falls back to the plain reason string when the
  // audit fields aren't populated (legacy tombstones).
  const reasonText =
    entry.removal_reason === "operator_excluded"
      ? `Excluded by ${entry.excluded_by_name || entry.excluded_by_email || "the operator"}${
          entry.excluded_at
            ? ` on ${new Date(entry.excluded_at).toLocaleDateString()}`
            : ""
        }`
      : baseReasonText;
  // A file expanded out of a synced archive has NO drive_file_id of its own -
  // Drive has no address for a file inside a zip - so it used to render as dead
  // plain text. Link it to the ARCHIVE instead: that is the thing the operator
  // can actually open, and it is where the file really lives. Prefer the sighting
  // for THIS row's archive (a member can sit in two, filed at different paths);
  // fall back to the first zip-member sighting that names one.
  const archiveDir = (entry.drive_path ?? "").split("/").slice(0, -1).join("/");
  const zipSightings = (entry.sightings ?? []).filter(
    (s) => s.source === "drive_sync_zip_member" && s.source_zip_drive_file_id,
  );
  const archiveSighting =
    zipSightings.find((s) => s.drive_path === archiveDir) ?? zipSightings[0];
  const isZipMember = entry.source === "drive_sync_zip_member";
  const archiveUrl = isZipMember
    ? driveUrl(archiveSighting?.source_zip_drive_file_id ?? null)
    : null;
  const url = driveUrl(entry.drive_file_id) ?? archiveUrl;
  // Every FILE-state affordance addresses the effective upload: on a dedup alias
  // (this Drive path's row was folded into a byte-identical winner) the winner
  // owns the bytes, extraction and facts. Equal to upload_id on ordinary rows.
  // The ?? keeps a browser holding a freshly-deployed bundle working against a
  // Lambda that hasn't rolled yet (the two stacks deploy separately).
  const fileId = entry.effective_upload_id ?? entry.upload_id;
  const factsQuery = useUploadExtracted(expanded ? fileId : null);
  const setFreshness = useSetUploadFreshness(projectId);
  const entryOutOfDate = isOutOfDate(entry);

  // On a dedup alias the stored copy is shared with the file's other places, so
  // say so on hover rather than leaving the operator to wonder why the same
  // document appears twice on the page.
  const archiveName =
    archiveSighting?.source_zip_filename ?? archiveDir.split("/").pop() ?? "the archive";
  const pathHint = archiveUrl
    ? // Say what the click actually does - it opens the ZIP, not this file, and an
      // operator who expects the document itself would otherwise be confused.
      `Inside "${archiveName}" - opens the archive in Drive (Drive can't link to a file inside a zip)`
    : entry.is_dedup_alias
      ? `${entry.drive_path ?? entry.original_name} - stored once and shared with this file's other places`
      : entry.drive_path;

  // The filename itself is the Drive link for active rows (replaces
  // the standalone "Open in Drive" button that was eating the actions
  // column width). Tombstones don't get a link - the file is gone /
  // excluded - and dismissable tombstones get a small Dismiss button.
  const nameNode = url && !isTombstone ? (
    <button
      type="button"
      // A click that merely ENDS a text selection must not navigate: the filename
      // is the thing an operator most often wants to copy (to search Drive, paste
      // into a ticket), and browsers still fire click after a drag-select.
      onClick={() => {
        if (window.getSelection()?.toString()) return;
        window.open(url, "_blank", "noopener,noreferrer");
      }}
      // Hover shows the Drive path so the operator can place the file in its
      // folder (the tree only shows the leaf name); falls back to the open hint
      // for a row with no path. Click still opens it in Drive.
      title={pathHint ?? "Open this file in Drive"}
      style={{
        background: "none",
        border: 0,
        padding: 0,
        color: "#2563eb",
        textDecoration: "underline",
        cursor: "pointer",
        fontSize: "inherit",
        fontFamily: "inherit",
        textAlign: "left",
        // Chrome and Safari apply `user-select: none` to buttons in their UA
        // stylesheet, so making the filename the Drive link silently made it
        // unselectable. It is a filename first and a link second.
        userSelect: "text",
      }}
    >
      {entry.original_name}
    </button>
  ) : (
    <span
      className={styles.fileName}
      style={
        isTombstone
          ? { textDecoration: "line-through", color: "#999" }
          : undefined
      }
      title={pathHint ?? entry.original_name}
    >
      {entry.original_name}
    </span>
  );

  return (
    <>
      <tr className={styles.row}>
        <td className={styles.td} style={{ width: 36 }} />
        <td className={styles.td}>
          {/* Wraps for the same reason the Gmail thread row does: the
              "Show extracted content …" control is longer than the space left
              beside a filename on a narrow screen. */}
          <div
            style={{
              paddingLeft: depth * 20,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              rowGap: 4,
            }}
          >
            <span style={{ marginRight: 6 }}>📄</span>
            {entryOutOfDate && (
              <OutOfDateIcon position="left" tooltip="Marked out of date" />
            )}
            {nameNode}
            {!isTombstone && <RegisterBadge documentId={fileId} projectId={projectId} />}
            {!isTombstone && (
              <ExtractionStatusButton
                uploadId={fileId}
                isExpanded={expanded}
                onToggle={onToggleExpand}
                chars={entry.extracted_text_chars}
                pages={entry.total_pages}
                heldUntil={entry.extraction_not_before}
              />
            )}
            {isTombstone && reasonText && (
              <span
                style={{
                  marginLeft: 8,
                  color: "#b03030",
                  fontSize: 11,
                  fontStyle: "italic",
                }}
                title={reasonText}
              >
                · {reasonText}
              </span>
            )}
          </div>
        </td>
        <td className={styles.tdSmall}>
          <span className={styles.sourceBadgeDrive} title="Auto-synced from Drive">
            Drive sync
          </span>
          {/* A file whose bytes also arrived elsewhere (a second Drive path, an
              email attachment, a manual upload) gets the shared collapsed
              "Seen in N places" summary, so the operator can see it is ONE
              document rather than wondering why a copy vanished. Single-sighting
              rows render nothing here - the Drive badge + the tree location
              already say everything a single Drive sighting would. */}
          {dedupeRenderedSightings(entry.sightings ?? []).length > 1 && (
            <span style={{ display: "block", marginTop: 3 }}>
              <SightingList sightings={entry.sightings} />
            </span>
          )}
        </td>
        <td className={styles.tdSmall}>
          {isTombstone ? (
            <span style={{ color: "#aaa" }}>{kindLabel(entry.kind)}</span>
          ) : (
            <span style={{ display: "inline-flex", gap: 6, alignItems: "baseline", fontSize: 12 }}>
              {kindLabel(entry.kind) && <span>{kindLabel(entry.kind)}</span>}
              <ReextractLink
                projectId={projectId}
                uploadId={fileId}
                extractionStatus={entry.extraction_status}
              />
            </span>
          )}
        </td>
        <td className={styles.tdSmall}>{formatBytes(entry.size_bytes)}</td>
        <td className={styles.tdSmall}>
          {isTombstone ? (
            <span title={`Removed ${relativeTime(entry.removed_at)}`}>removed</span>
          ) : (
            relativeTime(entry.last_pulled_at)
          )}
        </td>
        <td className={styles.tdSmall} style={{ whiteSpace: "nowrap" }}>
          {!isTombstone && (
            <>
              <button
                type="button"
                className={styles.openBtn}
                onClick={() =>
                  setFreshness.mutate({
                    project_id: projectId,
                    upload_id: fileId,
                    out_of_date: !entryOutOfDate,
                  })
                }
                disabled={setFreshness.isLoading}
                title={
                  entryOutOfDate
                    ? "Marked out of date. Click to mark fresh."
                    : "Mark this file as out of date"
                }
              >
                {entryOutOfDate ? "Mark fresh" : "Mark out of date"}
              </button>
              <button
                type="button"
                className={styles.deleteBtn}
                // Exclude / Reinstate / Dismiss act on the ROW (this Drive file),
                // not on the effective upload: excluding a deduped path must stop
                // syncing THIS path, not delete a file that is also filed
                // elsewhere. The BE drops the winner's sighting for it.
                onClick={() => onExclude(entry.upload_id)}
                disabled={excludeBusy}
                title="Exclude this file from Drive sync forever. The row tombstones and the recurring poll never re-pulls it."
                style={{ marginLeft: 6 }}
              >
                Exclude
              </button>
            </>
          )}
          {isTombstone && entry.removal_reason === "operator_excluded" && (
            <button
              type="button"
              className={styles.openBtn}
              onClick={() => onReinstate(entry.upload_id)}
              disabled={reinstateBusy}
              title="Bring this file back into Drive sync. The recurring poll will start pulling it again and extraction re-runs inline."
              style={{ marginRight: 6 }}
            >
              Reinstate
            </button>
          )}
          {isTombstone && (
            <button
              type="button"
              className={styles.deleteBtn}
              onClick={() => onDismiss(entry.upload_id)}
              disabled={dismissBusy}
              title="Hide this row from the default view"
            >
              Dismiss
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td />
          <td colSpan={6} style={{ padding: "8px 16px", background: "#fafafa" }}>
            {factsQuery.isLoading ? (
              <span style={{ color: "#888", fontSize: 12 }}>Loading extracted data…</span>
            ) : (
              <ExtractedContentPanel
                uploadId={fileId}
                chars={entry.extracted_text_chars}
                pages={entry.total_pages}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const FolderRow = ({
  node,
  depth,
  expanded,
  onToggle,
  onRefresh,
  refreshing,
}: {
  node: FolderNode;
  depth: number;
  expanded: boolean;
  onToggle: () => void;
  // Only passed for a top-level group, next to what the operator thinks of as
  // the source. It syncs the whole project, not just this group.
  onRefresh?: () => void;
  refreshing?: boolean;
}) => {
  const count = countTreeFiles(node);
  const { failed, outOfDate } = countFolderHealth(node);
  return (
    <tr
      className={styles.row}
      onClick={onToggle}
      style={{ cursor: "pointer", background: "#f5f7fb" }}
    >
      <td className={styles.td} style={{ width: 36 }} />
      <td className={styles.td} colSpan={6}>
        <div
          style={{
            paddingLeft: depth * 20,
            display: "flex",
            alignItems: "center",
            fontSize: 13,
            fontWeight: 600,
            color: "#1f2937",
          }}
        >
          <span style={{ width: 14, display: "inline-block", marginRight: 4 }}>
            <Chevron tree open={expanded} />
          </span>
          <span style={{ marginRight: 6 }}>📁</span>
          <span>
            {node.name}{" "}
            <span style={{ fontWeight: 400, color: "#6b7280" }}>({count})</span>
          </span>
          {failed > 0 && (
            <span
              style={{
                marginLeft: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "1px 6px",
                fontSize: 11,
                fontWeight: 600,
                color: "#991b1b",
                background: "transparent",
                border: "1px solid #f87171",
                borderRadius: 4,
              }}
              title={`${failed} file${failed === 1 ? "" : "s"} under this folder failed extraction. Expand the folder to find ${failed === 1 ? "it" : "them"}.`}
            >
              ✗ {failed} failed
            </span>
          )}
          {outOfDate > 0 && (
            <span
              style={{
                marginLeft: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "1px 6px",
                fontSize: 11,
                fontWeight: 600,
                color: "#b45309",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 4,
              }}
              title={`${outOfDate} file${outOfDate === 1 ? "" : "s"} under this folder ${outOfDate === 1 ? "is" : "are"} marked out of date. Expand the folder to find ${outOfDate === 1 ? "it" : "them"}.`}
            >
              ⏱ {outOfDate} out of date
            </span>
          )}
          {onRefresh && (
            <button
              type="button"
              className={styles.openBtn}
              style={{ marginLeft: 12, padding: "2px 10px", fontSize: 11 }}
              disabled={refreshing}
              onClick={(e) => {
                e.stopPropagation();
                onRefresh();
              }}
              title="Pull every file in this project's Drive subscription now."
            >
              {refreshing ? "Syncing…" : "Sync now"}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

interface RecursiveProps {
  node: FolderNode;
  depth: number;
  projectId: string;
  isFolderOpen: (node: FolderNode, depth: number) => boolean;
  toggleFolder: (key: string) => void;
  expandedFiles: Set<string>;
  toggleFile: (uploadId: string) => void;
  onDismiss: (uploadId: string) => void;
  dismissBusy: boolean;
  onExclude: (uploadId: string) => void;
  excludeBusy: boolean;
  onReinstate: (uploadId: string) => void;
  reinstateBusy: boolean;
  // Attached to the FolderRow only at depth 0 (a top-level group). Sync is
  // project-wide on the BE, so nested folders don't get their own button.
  onRootRefresh: () => void;
  rootRefreshing: boolean;
}

const FolderSubtree = (props: RecursiveProps) => {
  const {
    node,
    depth,
    projectId,
    expandedFiles,
    toggleFile,
    onDismiss,
    dismissBusy,
    onExclude,
    excludeBusy,
    onReinstate,
    reinstateBusy,
  } = props;
  const visibleChildren = useMemo(
    () =>
      // Hide folders excluded wholesale by the project's Drive-sync config -
      // every row would be an un-actionable "Folder is excluded" tombstone.
      sortedChildren(node).filter((child) => !isEntirelyFolderExcluded(child)),
    [node],
  );
  const files = useMemo(() => sortedFiles(node), [node]);
  return (
    <>
      {visibleChildren.map((child) => (
        <FolderSubtreeOrRow key={child.key} {...props} node={child} />
      ))}
      {files.map((entry) => (
        <FileRow
          key={entry.upload_id}
          entry={entry}
          projectId={projectId}
          depth={depth}
          expanded={expandedFiles.has(entry.upload_id)}
          onToggleExpand={() => toggleFile(entry.upload_id)}
          onExclude={onExclude}
          excludeBusy={excludeBusy}
          onReinstate={onReinstate}
          reinstateBusy={reinstateBusy}
          onDismiss={onDismiss}
          dismissBusy={dismissBusy}
        />
      ))}
    </>
  );
};

const FolderSubtreeOrRow = (props: RecursiveProps) => {
  const { node, depth, isFolderOpen, toggleFolder, onRootRefresh, rootRefreshing } = props;
  const isOpen = isFolderOpen(node, depth);
  return (
    <>
      <FolderRow
        node={node}
        depth={depth}
        expanded={isOpen}
        onToggle={() => toggleFolder(node.key)}
        onRefresh={depth === 0 ? onRootRefresh : undefined}
        refreshing={depth === 0 ? rootRefreshing : undefined}
      />
      {isOpen && <FolderSubtree {...props} depth={depth + 1} />}
    </>
  );
};

export const DriveSyncedFilesTree = ({ projectId }: Props) => {
  const { data, isLoading } = useUploadsTree(projectId);
  // Read the registered Drive folder name from project state so we can
  // render the root folder row even before the first sync lands (no
  // files = no drive_path = no inferred tree). The folder name was
  // captured at configuration time; we already know it.
  const project = useProject(projectId);
  const driveSync = project.data?.drive_sync as DriveSyncState | null | undefined;
  const dismiss = useDismissUpload();
  const excludeFile = useExcludeDriveFile();
  const reinstateFile = useReinstateDriveFile();
  const { user: kindeUser } = useKindeAuth();
  // Resolve the operator display name on the FE so the tombstone reads
  // as a human identity ("Noaa Cohn", "noaa@inrange.io") rather than the
  // opaque Kinde sub. Mirrors ProjectProcurementTab's dismissal pattern.
  const actorName =
    [kindeUser?.given_name, kindeUser?.family_name].filter(Boolean).join(" ") ||
    kindeUser?.email ||
    "";
  // Manual refresh is exposed on the Files tab directly so the
  // operator never has to bounce to the overview card to pull new
  // files. Works even when the 15-minute schedule is paused (the BE
  // route no longer gates on drive_sync.enabled for manual triggers).
  const triggerSync = useTriggerDriveSync();
  const sync = useDriveSyncOperation(projectId);
  const syncInFlight = sync.inFlight;
  const syncError = sync.error;
  const setSyncError = sync.setError;
  const handleRootRefresh = () => {
    // Don't swallow the trigger error: a hard sync-now failure used to vanish
    // here (the operator saw "Syncing..." then nothing). Surface it, and the
    // persisted last_sync_status/error shows on the SyncStatusLine for the
    // soft "drive_error" / "folder_gone" / "auth_failed" outcomes.
    setSyncError(null);
    triggerSync
      .mutateAsync({ project_id: projectId })
      .then((lro) => sync.track(lro.operation_key))
      .catch((e: unknown) =>
        setSyncError(e instanceof Error ? e.message : String(e)),
      );
  };
  const { isOpen: isFolderOpen, toggle: toggleFolder } = useDriveTreeOpenState();
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());

  // Every configured source shows even before it has files, so the operator keeps
  // the anchor that says it is wired up, plus the Sync now affordance.
  const roots = driveSync?.roots;
  const hasTrackedFiles = (driveSync?.files?.length ?? 0) > 0;
  const groups = useMemo(
    () =>
      buildDriveFilesTree(data?.drive_sync_files ?? [], {
        groupIdOf: (entry) => entry.parent_drive_folder_id,
        trackedGroupId: TRACKED_PARENT,
        rootNames: new Map((roots ?? []).map((r) => [r.folder_id, r.folder_name])),
        alwaysShow: [
          ...(roots ?? []).map((r) => r.folder_id),
          ...(hasTrackedFiles ? [TRACKED_PARENT] : []),
        ],
      }),
    [data, roots, hasTrackedFiles],
  );
  // Every row's extraction status from ONE batched request, not one per row -
  // see GmailSyncedFilesTree.
  const extractedIds = useMemo(
    () =>
      (data?.drive_sync_files ?? [])
        .filter((e) => e.status !== "removed")
        .map((e) => e.upload_id),
    [data],
  );

  const toggleFile = (uploadId: string) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(uploadId)) next.delete(uploadId);
      else next.add(uploadId);
      return next;
    });
  };

  const handleDismiss = (uploadId: string) => {
    dismiss.mutate({ upload_id: uploadId, project_id: projectId });
  };

  const handleExclude = (uploadId: string) => {
    if (
      !window.confirm(
        "Exclude this file from Drive sync? The row will be tombstoned and the recurring sync will never re-pull it. Audit info (your name + the time) is captured on the tombstone.",
      )
    ) {
      return;
    }
    excludeFile.mutate({
      project_id: projectId,
      upload_id: uploadId,
      actor_name: actorName,
    });
  };

  const handleReinstate = (uploadId: string) => {
    reinstateFile.mutate({ project_id: projectId, upload_id: uploadId });
  };

  if (isLoading || project.isLoading) {
    return <p style={{ color: "#888", fontSize: 13, padding: 12 }}>Loading drive-synced files…</p>;
  }
  // No groups means no configured roots, tracked files or synced rows: the
  // project has no drive_sync wiring. Gated on project.isLoading above so this
  // doesn't flash before the first /projects/get response.
  if (groups.length === 0) {
    return (
      <p style={{ color: "#888", fontSize: 13, padding: 12 }}>
        Drive subscription is not configured yet. Set it up from the
        Settings tab.
      </p>
    );
  }
  return (
    <UploadsExtractedProvider uploadIds={extractedIds}>
      {driveSync && (
        <div style={{ marginBottom: 8, paddingLeft: 2 }}>
          <SyncStatusLine
            lastSuccessfulSyncAt={driveSync.last_successful_sync_at}
            lastSyncedAt={driveSync.last_synced_at}
            lastSyncStatus={driveSync.last_sync_status}
            lastSyncError={driveSync.last_sync_error}
            walkTruncated={driveSync.last_sync_walk_truncated}
            newFolders={driveSync.last_sync_new_folders}
          />
        </div>
      )}
      {syncError && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 8,
            fontSize: 12,
            color: "#991b1b",
            background: "transparent",
            border: "1px solid #f87171",
            borderRadius: 6,
          }}
        >
          {syncError}
        </div>
      )}
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th} style={{ width: 36 }} />
            <th className={styles.th}>Name</th>
            <th className={styles.th}>Source</th>
            <th className={styles.th}>Type</th>
            <th className={styles.th}>Size</th>
            <th className={styles.th}>Pulled</th>
            <th className={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <FolderSubtreeOrRow
              key={group.key}
              node={group}
              depth={0}
              projectId={projectId}
              isFolderOpen={isFolderOpen}
              toggleFolder={toggleFolder}
              expandedFiles={expandedFiles}
              toggleFile={toggleFile}
              onDismiss={handleDismiss}
              dismissBusy={dismiss.isLoading}
              onExclude={handleExclude}
              excludeBusy={excludeFile.isLoading}
              onReinstate={handleReinstate}
              reinstateBusy={reinstateFile.isLoading}
              onRootRefresh={handleRootRefresh}
              rootRefreshing={syncInFlight || triggerSync.isLoading}
            />
          ))}
        </tbody>
      </table>
    </UploadsExtractedProvider>
  );
};
